export const ACCEPTED_EXTENSIONS = ['mp4', 'jpg', 'jpeg', 'png', 'bmp'];
export const MAX_FILE_SIZE_MB = 20;
export const MAX_VIDEO_FILES = 5;
export const MAX_TOTAL_FILES = 10;
export const DEFAULT_NON_N62_RESOLUTION = '720x1280';

export const getExtension = (filename = '') => {
  const parts = String(filename).toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
};

export const bytesToMb = (bytes = 0) => {
  return `${(Number(bytes) / 1024 / 1024).toFixed(2)} MB`;
};

export const parseMaybeJSON = (value, fallback = []) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return fallback;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch (err) {
      return fallback;
    }
  }

  return fallback;
};

export const toBool = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  }

  return false;
};

export const normalizeNewAdvertiseFile = (file) => ({
  key: `new-${file.name}-${file.size}-${file.lastModified}`,
  name: file.name,
  size: Number(file.size || 0),
  type: file.type || '',
  ext: getExtension(file.name),
  existing: false,
  fileObject: file,
});

export const normalizeExistingAdvertiseFile = (file, index = 0) => ({
  key: `existing-${
    file.AdvertiseFileId || file.FilePath || file.StoredFileName || file.OriginalFileName || index
  }`,
  name: file.OriginalFileName || file.name || '',
  size: Number(file.FileSize ?? file.size ?? 0),
  type: file.MimeType || file.type || '',
  ext: String(file.FileExt || getExtension(file.OriginalFileName || file.name || '')).toLowerCase(),
  existing: true,
  AdvertiseFileId: file.AdvertiseFileId || null,
  OriginalFileName: file.OriginalFileName || file.name || '',
  StoredFileName: file.StoredFileName || null,
  FilePath: file.FilePath || null,
  FileExt: String(file.FileExt || getExtension(file.OriginalFileName || file.name || '')).toLowerCase(),
  MimeType: file.MimeType || file.type || null,
  FileSize: Number(file.FileSize ?? file.size ?? 0),
  SortOrder: Number(file.SortOrder || index + 1),
});

export const getAdvertiseType = (files = []) => {
  const extList = files.map((file) => String(file.ext || file.FileExt || '').toLowerCase());

  if (extList.includes('mp4')) return 'Video';
  if (extList.some((ext) => ['jpg', 'jpeg', 'png', 'bmp'].includes(ext))) return 'Zdjęcia';

  return 'Empty';
};

const appendScalar = (formData, key, value) => {
  if (value === undefined || value === null) {
    formData.append(key, '');
    return;
  }

  formData.append(key, String(value));
};

const normalizeTerminalSerialsForApi = (terminalSerials = []) => {
  return (Array.isArray(terminalSerials) ? terminalSerials : [])
    .map((terminal) => ({
      TerminalId: Number(terminal?.TerminalId || 0) || null,
      SerialNo: String(
        terminal?.SerialNo ||
          terminal?.serialNo ||
          terminal?.SerialNumber ||
          terminal?.serialNumber ||
          ''
      ).trim(),
    }))
    .filter((terminal) => terminal.SerialNo);
};

export const buildAdvertiseFormData = (draft) => {
  const formData = new FormData();
  const files = Array.isArray(draft?.FilesMeta) ? draft.FilesMeta : [];

  const existingFiles = files
    .filter((file) => file.existing)
    .map((file, index) => ({
      OriginalFileName: file.OriginalFileName || file.name || '',
      StoredFileName: file.StoredFileName || '',
      FilePath: file.FilePath || '',
      FileExt: String(file.FileExt || file.ext || '').toLowerCase(),
      MimeType: file.MimeType || file.type || '',
      FileSize: Number(file.FileSize ?? file.size ?? 0),
      SortOrder: index + 1,
    }));

  files
    .filter((file) => !file.existing)
    .forEach((file) => {
      if (file.fileObject instanceof File) {
        formData.append('Files', file.fileObject, file.fileObject.name || file.name || 'file');
      }
    });

  appendScalar(formData, 'Name', String(draft?.Name || '').trim());
  appendScalar(formData, 'Priority', Number(draft?.Priority ?? 0));
  appendScalar(formData, 'StartDelaySec', Number(draft?.StartDelaySec ?? 15));
  appendScalar(formData, 'ImageDurationSec', Number(draft?.ImageDurationSec ?? 30));
  appendScalar(formData, 'MoveSensitivity', Number(draft?.MoveSensitivity ?? 0));
  appendScalar(formData, 'ShowMessage', draft?.ShowMessage ? 1 : 0);
  appendScalar(formData, 'IsActive', draft?.IsActive ? 1 : 0);
  appendScalar(formData, 'PublishStatus', draft?.PublishStatus || 'NotPublished');
  appendScalar(formData, 'PackageType', draft?.PackageType || getAdvertiseType(files));
  appendScalar(formData, 'ScreenRatioKey', draft?.ScreenRatioKey || '');
  appendScalar(formData, 'Comment', draft?.Comment || '');

  formData.append(
    'TerminalIds',
    JSON.stringify((draft?.TerminalIds || []).map((id) => Number(id)).filter(Number.isFinite))
  );

  formData.append(
    'TerminalSerials',
    JSON.stringify(normalizeTerminalSerialsForApi(draft?.TerminalSerials || []))
  );

  formData.append('ExistingFiles', JSON.stringify(existingFiles));

  return formData;
};